import apiService from './apiService';

// ── Agenda TO Contacts — Phase C (Teams / T5) ──────────────────────────────
// Thin wrappers over the /api/teams blueprint (app/routes/teams.py). The
// mutual-handshake TEAMS layer: a team fans out real membership/invitation rows
// and structural asset shares (docs/conversations/books/events). Response shapes
// (kept in sync with team_service.serialize_*):
//   listTeams()   → { teams: [{ id, name, description, color, member_count,
//                               viewer_role, is_creator, created_at }] }
//   getTeam(id)   → { team: { id, name, description, color, viewer_role,
//                             is_creator, created_at,
//                             members: [{ user_id, email, username, role, is_creator }],
//                             shares:  { document:[{share_id,id,filename,...}],
//                                        conversation:[{share_id,id,title,...}],
//                                        book:[{share_id,id,name,color,...}],
//                                        board:[{share_id,id,name,color,...}],
//                                        event:[{share_id,id,title,start_time,...}] } } }
//   createTeam    → { team: <detail> }   (201)
//   deleteTeam    → { deleted, summary:{members,shares,event_invitations_revoked,...} }
//   removeMember  → { removed, left, summary }
//   shareAsset    → { share:{share_id,team_id,kind,asset_id}, book_members_added,
//                     members_added, members_skipped:[user_id],
//                     owner_grants_clamped:[user_id] }
//   createTeamEvent → { event:{id,title,start_time,...}, invited }
export const teamService = {
    async listTeams() {
        return apiService.get('/api/teams');
    },
    async getTeam(teamId) {
        return apiService.get(`/api/teams/${teamId}`);
    },
    // { name, description?, color?, members?[{user_id, role}], member_user_ids?[] }
    //
    // `members` is the role-carrying shape ("Update team creation part 2"):
    // each entry's role ('owner' | 'member') becomes that person's TeamMember
    // role, and is later carried onto the Board/Book membership rows a team
    // share mints. `member_user_ids` is the original flat list (everyone a plain
    // member) — still accepted, still what the chat tool sends. Sending both is
    // fine: the BE (team_service.parse_member_specs) merges them, the
    // role-carrying entry winning.
    async createTeam(data) {
        return apiService.post('/api/teams', data || {});
    },
    // { name?, description?, color? } — owner only.
    async updateTeam(teamId, updates) {
        return apiService.patch(`/api/teams/${teamId}`, updates || {});
    },
    // CREATOR only (Smithereens).
    async deleteTeam(teamId) {
        return apiService.delete(`/api/teams/${teamId}`);
    },
    // { user_id | email, role? } — owner only; ContactLink gate vs the adder.
    async addMember(teamId, { userId = null, email = null, role = 'member' } = {}) {
        const body = { role };
        if (userId) body.user_id = userId;
        if (email) body.email = email;
        return apiService.post(`/api/teams/${teamId}/members`, body);
    },
    // Owner kicks anyone (not the creator); a member removes THEMSELVES (leave).
    async removeMember(teamId, userId) {
        return apiService.delete(`/api/teams/${teamId}/members/${userId}`);
    },
    // { kind:'document'|'conversation'|'book'|'board'|'event', asset_id, role_overrides? }
    // 'book'/'board' fan out REAL team_id-stamped membership rows; anyone who
    // already holds an INDIVIDUAL grant is skipped (#194 no dual membership).
    // Each new row takes the member's TEAM role (owner→owner) unless
    // `role_overrides` names a different one for them (part 2). Only the asset's
    // OG creator can mint owner rows (#195): a share by a secondary owner lands
    // the team's owners as plain members, reported in `owner_grants_clamped`.
    async shareAsset(teamId, { kind, assetId, roleOverrides = null } = {}) {
        const body = { kind, asset_id: assetId };
        if (roleOverrides) body.role_overrides = roleOverrides;
        return apiService.post(`/api/teams/${teamId}/shares`, body);
    },
    // sharer or team owner. kind='book'/'board' also deletes the team-stamped memberships.
    async unshareAsset(teamId, shareId) {
        return apiService.delete(`/api/teams/${teamId}/shares/${shareId}`);
    },
    // Member-only: creates the event + fans out a CalendarEventInvitation per other
    // member (bells + dual-write). { title, start_time, end_time?, location?, description? }
    async createTeamEvent(teamId, data) {
        return apiService.post(`/api/teams/${teamId}/events`, data || {});
    },
};

export default teamService;
